import type { ReactNode } from 'react'
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useSearchParams } from 'react-router-dom'
import { Shell } from '@/components/Shell'
import { Banner, LinkButton } from '@/components/ui'
import { DraftEditorPage } from '@/features/application'
import { BoardPage } from '@/features/board'
import { DemoPage } from '@/features/demo'
import { AboutPage, HomePage, PermitListPage } from '@/features/home'
import { LearningProgressPage } from '@/features/learning-host'
import { TrainingProgrammeHost } from '@/features/learning-host/TrainingProgrammeHost'
import { LicenceRegisterPage } from '@/features/licences'
import { PermitDetailPage } from '@/features/permits/PermitDetail'
import { PolicyPage } from '@/features/policy'
import { PrintPage } from '@/features/print'
import { isAccountBuild, isConfigured } from '@/lib/env'
// Identity (0022): the guard checks a persona session in the demo and a real signed-in
// account in operational/staging builds (which have no /demo, persona menu or join codes).
import { DemoRoute, PeoplePage, RequireSession, SignInPage } from '@/features/account'
import { RegistryPage } from '@/features/registry/RegistryPage'

const guard = (el: ReactNode) => <RequireSession>{el}</RequireSession>

/**
 * Help & Learning. Reading needs no persona and no sign-in. Signed out, progress stays in
 * this browser. Signed in (a persona session), progress belongs to that person and is
 * kept on the server, so it follows them to any device and an account administrator can
 * see it. Practice links only open the existing demo page (where nothing starts until the
 * user presses Start), and only in a configured synthetic demonstration build.
 * ?step=<id> or ?lesson=<id> opens contextual help.
 */
function LearnRoute() {
  const [params] = useSearchParams()
  return <TrainingProgrammeHost initialModuleId={params.get('module') ?? undefined} initialStepId={params.get('step') ?? undefined} initialLessonId={params.get('lesson') ?? undefined} />
}

const router = createBrowserRouter([
  {
    element: <Shell />,
    children: [
      { path: '/', element: guard(<HomePage />) },
      { path: '/demo', element: <DemoRoute><DemoPage /></DemoRoute> },
      { path: '/sign-in', element: <SignInPage /> },
      { path: '/admin/people', element: guard(<PeoplePage />) },
      { path: '/admin/registry', element: guard(<RegistryPage />) },
      { path: '/about', element: <AboutPage /> },
      // Account builds: the programme records completion against the signed-in account, so
      // /learn needs a real sign-in like every other page; the demo keeps guest reading.
      { path: '/learn', element: isAccountBuild ? guard(<LearnRoute />) : <LearnRoute /> },
      { path: '/learn/progress', element: guard(<LearningProgressPage />) },
      { path: '/admin/licences', element: guard(<LicenceRegisterPage />) },
      { path: '/permits', element: guard(<PermitListPage />) },
      { path: '/permits/new', element: guard(<DraftEditorPage />) },
      { path: '/permits/:id', element: guard(<PermitDetailPage />) },
      { path: '/permits/:id/edit', element: guard(<DraftEditorPage />) },
      { path: '/board', element: guard(<BoardPage />) },
      { path: '/policy', element: guard(<PolicyPage />) },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
  // Print renders without the app chrome.
  { path: '/permits/:id/print', element: guard(<PrintPage />) },
])

/** Without a backend only Help & Learning works; every data page explains why it cannot. */
function UnconfiguredLayout() {
  return (
    <div className="min-h-dvh">
      <main className="mx-auto max-w-5xl px-4 py-4">
        <Outlet />
      </main>
    </div>
  )
}

function NotConfigured() {
  return (
    <div className="mx-auto max-w-xl space-y-3 p-2">
      <Banner tone="bad" title="This build is not configured">
        VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY were not set when the app was built, so permits cannot be shown.
      </Banner>
      <LinkButton to="/learn" variant="secondary">
        Open Help &amp; Learning
      </LinkButton>
    </div>
  )
}

const unconfiguredRouter = createBrowserRouter([
  {
    element: <UnconfiguredLayout />,
    children: [
      { path: '/learn', element: <LearnRoute /> },
      { path: '*', element: <NotConfigured /> },
    ],
  },
])

export function App() {
  return <RouterProvider router={isConfigured ? router : unconfiguredRouter} />
}
